import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AgentService, AgentDto, ZoneDto, AgentDtoPaginatedResponse } from '../../../services/agent.service';
import { AgentLeaveService, AgentLeaveQuotaDto, UpdateAgentLeaveConfigDto } from '../../../services/agent-leave.service';
import { AuthService } from '../../../services/auth.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';
import { filter, take } from 'rxjs';

@Component({
  selector: 'app-agent-quotas',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PaginationComponent],
  templateUrl: './agent-quotas.component.html',
  styleUrl: './agent-quotas.component.css'
})
export class AgentQuotasComponent implements OnInit {
  agents: AgentDto[] = [];
  zones: ZoneDto[] = [];
  quotas: { [agentId: string]: AgentLeaveQuotaDto } = {};
  selectedZoneFilter = '';
  selectedAgentId = '';
  dropdownAgents: AgentDto[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;

  // Config Override Modal Dialog
  showConfigModal = false;
  configAgent: AgentDto | null = null;
  configDto: UpdateAgentLeaveConfigDto = {};
  currentQuota: AgentLeaveQuotaDto | null = null;
  savingConfig = false;

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };

  constructor(
    private agentService: AgentService,
    private leaveService: AgentLeaveService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable().pipe(
      filter((initialized: boolean) => initialized),
      take(1)
    ).subscribe(() => {
      this.loadZones();
      this.loadDropdownAgents();
      this.loadAgents();
    });
  }

  loadZones(): void {
    this.agentService.getAllZones().subscribe({
      next: (res: ZoneDto[]) => {
        this.zones = res || [];
      },
      error: (err: any) => {
        console.error('Failed to load zones', err);
      }
    });
  }

  loadDropdownAgents(): void {
    // PageSize is 100 to stay within the backend maximum allowed validation limit of 100
    this.agentService.getAgentList(undefined, true, 0, 100).subscribe({
      next: (res) => {
        this.dropdownAgents = res.items || [];
      },
      error: (err: any) => {
        console.error('Failed to load dropdown agents', err);
      }
    });
  }

  loadAgents(): void {
    this.isLoading = true;

    if (this.selectedAgentId) {
      this.agentService.getAgentById(this.selectedAgentId).subscribe({
        next: (agent: AgentDto) => {
          // If a zone filter is selected, check if this agent belongs to that zone
          if (this.selectedZoneFilter && !agent.zones.includes(this.selectedZoneFilter)) {
            this.agents = [];
            this.totalCount = 0;
            this.totalPages = 1;
            this.isLoading = false;
            return;
          }

          this.agents = [agent];
          this.totalCount = 1;
          this.totalPages = 1;
          this.loadQuotasForAgents();
        },
        error: (err: any) => {
          console.error('Failed to load agent by ID', err);
          this.showNotification('Failed to load agent config', 'error');
          this.agents = [];
          this.totalCount = 0;
          this.totalPages = 1;
          this.isLoading = false;
        }
      });
      return;
    }

    const pageIndex = this.currentPage - 1;
    this.agentService.getAgentList(
      this.selectedZoneFilter || undefined,
      true, // Active agents only
      pageIndex,
      this.pageSize
    ).subscribe({
      next: (res: AgentDtoPaginatedResponse) => {
        this.agents = res.items || [];
        this.totalCount = res.totalCount || 0;
        this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
        
        // Fetch leave quotas for loaded agents
        this.loadQuotasForAgents();
      },
      error: (err: any) => {
        console.error('Failed to load agents', err);
        this.showNotification('Failed to load agents list', 'error');
        this.isLoading = false;
      }
    });
  }

  loadQuotasForAgents(): void {
    if (this.agents.length === 0) {
      this.isLoading = false;
      return;
    }

    let loadedCount = 0;
    const totalAgents = this.agents.length;
    this.agents.forEach(agent => {
      this.leaveService.getAgentQuota(agent.agentId).subscribe({
        next: (quota: AgentLeaveQuotaDto) => {
          this.quotas[agent.agentId] = quota;
          loadedCount++;
          if (loadedCount === totalAgents) {
            this.isLoading = false;
          }
        },
        error: (err: any) => {
          console.error(`Failed to load quota for agent ${agent.agentId}`, err);
          loadedCount++;
          if (loadedCount === totalAgents) {
            this.isLoading = false;
          }
        }
      });
    });
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadAgents();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadAgents();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadAgents();
  }

  openConfigModal(agent: AgentDto): void {
    this.configAgent = agent;
    this.showConfigModal = true;
    this.currentQuota = null;

    this.leaveService.getAgentQuota(agent.agentId).subscribe({
      next: (res: AgentLeaveQuotaDto) => {
        this.currentQuota = res;
        this.configDto = {
          allowedLeavesPerMonth: res.allowedLeavesPerMonth,
          informedLeavePenaltyAmount: res.informedLeavePenaltyAmount,
          uninformedLeavePenaltyAmount: res.uninformedLeavePenaltyAmount,
          availableLeavesBalance: res.availableLeavesBalance
        };
      },
      error: (err: any) => {
        console.error('Failed to load agent quota config', err);
        this.showNotification('Failed to load quota configuration', 'error');
      }
    });
  }

  closeConfigModal(): void {
    this.showConfigModal = false;
    this.configAgent = null;
    this.configDto = {};
    this.currentQuota = null;
  }

  submitConfigOverride(): void {
    if (!this.configAgent) return;

    this.savingConfig = true;
    this.leaveService.updateAgentConfig(this.configAgent.agentId, this.configDto).subscribe({
      next: () => {
        this.showNotification('Agent quota configuration updated successfully', 'success');
        this.savingConfig = false;
        
        // Refresh the quota for this agent
        if (this.configAgent) {
          const agentId = this.configAgent.agentId;
          this.leaveService.getAgentQuota(agentId).subscribe((quota: AgentLeaveQuotaDto) => {
            this.quotas[agentId] = quota;
          });
        }
        
        this.closeConfigModal();
      },
      error: (err: any) => {
        console.error('Failed to update config', err);
        this.showNotification(err.error?.message || 'Failed to update configuration', 'error');
        this.savingConfig = false;
      }
    });
  }

  getZoneNames(zoneIds: string[]): string {
    if (!zoneIds || zoneIds.length === 0) {
      return 'No zones assigned';
    }
    return zoneIds.map(id => {
      const zone = this.zones.find(z => z.id === id);
      return zone ? zone.name : id;
    }).join(', ');
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.notification = {
      show: true,
      message,
      type
    };
    setTimeout(() => {
      this.notification.show = false;
    }, 4000);
  }
}
